import type { ProblemDetails } from '@jupi/shared'

// The API client for both SPAs.
//
// The session lives in httpOnly cookies, so nothing here reads or stores a
// token -- every request just sets credentials: 'include' and lets the
// browser attach them. That is what makes an XSS unable to steal the
// session.

export class ApiError extends Error {
  readonly status: number
  readonly problem: ProblemDetails | null
  // Per-field validation messages, when the server sent them.
  readonly fieldErrors: Record<string, string[]>
  // Booking-rule violation codes, so the UI can render each in the
  // member's own language instead of echoing a server-side sentence.
  readonly violations: { code: string; [key: string]: unknown }[]

  constructor(status: number, message: string, problem: ProblemDetails | null, body?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.problem = problem
    const raw = body as { errors?: Record<string, string[]>; violations?: { code: string }[] } | null
    this.fieldErrors = raw?.errors ?? {}
    this.violations = raw?.violations ?? []
  }
}

const API_BASE_URL = (import.meta.env?.VITE_API_BASE_URL as string | undefined) ?? ''

// For URLs the browser loads itself (an <img src>), which bypass apiFetch.
export const apiUrl = (path: string) => `${API_BASE_URL}${path}`

// Requests that must never trigger a refresh-and-retry: /login failing with
// 401 means the password was wrong, and retrying /refresh on its own 401
// would loop. The other sign-in routes are the same -- their 401 is about
// the link, code or passkey, not the session.
const NO_REFRESH_PATHS = [
  '/api/auth/login',
  '/api/auth/refresh',
  '/api/auth/logout',
  '/api/auth/magic-link/verify',
  '/api/auth/magic-link/code',
  '/api/auth/passkeys/login/options',
  '/api/auth/passkeys/login/verify',
  '/api/auth/password/reset',
]

function rawFetch(path: string, options: RequestInit) {
  const hasJsonBody = options.body != null && !(options.body instanceof FormData)
  return fetch(`${API_BASE_URL}${path}`, {
    ...options,
    credentials: 'include',
    headers: {
      ...(hasJsonBody ? { 'Content-Type': 'application/json' } : {}),
      ...options.headers,
    },
  })
}

// One shared refresh across concurrent 401s. A page that fires five queries
// at once would otherwise send five refreshes, and since refresh tokens
// rotate, four of them would present an already-spent token -- which the
// server correctly treats as a leaked cookie and responds to by revoking
// every session the member has.
let refreshInFlight: Promise<boolean> | null = null

function tryRefresh(): Promise<boolean> {
  refreshInFlight ??= rawFetch('/api/auth/refresh', { method: 'POST' })
    .then((res) => res.ok)
    .catch(() => false)
    .finally(() => {
      refreshInFlight = null
    })
  return refreshInFlight
}

// One request, with the single refresh-and-retry on a 401, and an ApiError
// for any other failure. The body is left for the caller to read.
async function fetchOk(path: string, options: RequestInit): Promise<Response> {
  let res = await rawFetch(path, options)

  if (res.status === 401 && !NO_REFRESH_PATHS.includes(path)) {
    if (await tryRefresh()) res = await rawFetch(path, options)
  }

  if (!res.ok) {
    const body = await res.json().catch(() => null)
    const problem = body as ProblemDetails | null
    throw new ApiError(res.status, problem?.detail ?? res.statusText, problem, body)
  }
  return res
}

export async function apiFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const res = await fetchOk(path, options)
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

export const api = {
  get: <T,>(path: string) => apiFetch<T>(path),
  // A binary download (a PDF), so a failure can be shown in the app instead
  // of as the API's JSON error in a browser tab.
  blob: async (path: string) => (await fetchOk(path, {})).blob(),
  post: <T,>(path: string, body?: unknown) =>
    apiFetch<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) }),
  patch: <T,>(path: string, body: unknown) =>
    apiFetch<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  put: <T,>(path: string, body: unknown) =>
    apiFetch<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  delete: <T,>(path: string, body?: unknown) =>
    apiFetch<T>(path, { method: 'DELETE', body: body === undefined ? undefined : JSON.stringify(body) }),
}
